import { Grid, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";

import Input from "../Input";

import type { PersonNameChildAttribute, PersonNameValue } from "./types";
import { usePersonName } from "./usePersonName";

export interface InputPersonNameProps {
  /** 氏名の値 */
  value: PersonNameValue;
  /** 値変更時のコールバック */
  onChange: (value: PersonNameValue) => void;
  /** 子属性（各フィールドのname/required/readonlyを個別に指定） */
  childAttributes?: PersonNameChildAttribute[];
  /** 読み取り専用フラグ */
  readonly?: boolean;
  /** 無効化フラグ */
  disabled?: boolean;
}

/**
 * 氏名入力コンポーネント
 *
 * 姓・名・姓カナ・名カナの4フィールドを1つのコンポーネントで管理します。
 * childAttributesプロパティで各フィールドのname/required/readonlyを個別に指定できます。
 * 表示順序は固定で、姓→名→姓カナ→名カナの順序で表示されます。
 */
export const InputPersonName = ({
  value,
  onChange,
  childAttributes,
  readonly = false,
  disabled = false,
}: InputPersonNameProps) => {
  const { nameFields, kanaFields, isFieldReadonly, handleFieldChange } =
    usePersonName({
      value,
      onChange,
      childAttributes,
      readonly,
    });

  /**
   * フィールドをレンダリングするヘルパー
   */
  const renderField = (child: PersonNameChildAttribute) => {
    const fieldReadonly = isFieldReadonly(child);

    return (
      <VStack key={child.attributeKey} className="gap-1">
        <Text variant="label" className="text-neutral-muted-on-fill">
          {child.name}
        </Text>
        <Input
          value={value[child.fieldKey] ?? ""}
          onChange={(e) => handleFieldChange(child.fieldKey, e.target.value)}
          disabled={disabled || fieldReadonly}
          readOnly={fieldReadonly}
        />
      </VStack>
    );
  };

  // childAttributesが空の場合は何も表示しない
  if (nameFields.length === 0 && kanaFields.length === 0) {
    return null;
  }

  return (
    <VStack className="gap-4">
      {/* 姓・名 */}
      {nameFields.length > 0 && (
        <Grid columns={2} className="gap-4">
          {nameFields.map(renderField)}
        </Grid>
      )}

      {/* 姓カナ・名カナ */}
      {kanaFields.length > 0 && (
        <Grid columns={2} className="gap-4">
          {kanaFields.map(renderField)}
        </Grid>
      )}
    </VStack>
  );
};
